import { supabase } from "./supabase";

export class ApiError extends Error {
  constructor(
    public readonly status: number,
    message: string,
    public readonly body?: unknown,
  ) {
    super(message);
    this.name = "ApiError";
  }
}

function parseApiErrorPayload(status: number, data: unknown): { message: string; detail?: string } {
  if (typeof data === "string") {
    const trimmed = data.trim();
    const looksLikeHtml = /^<!doctype\s+html/i.test(trimmed) || /^<html[\s>]/i.test(trimmed);
    if (looksLikeHtml) {
      if (status === 503) {
        return { message: "The vendor portal is temporarily unavailable while the app updates. Please try again in a minute." };
      }
      return { message: `The vendor portal returned an unexpected error (${status}). Please try again.` };
    }
    return { message: trimmed.length > 0 ? trimmed : `Request failed: ${status}` };
  }
  if (!data || typeof data !== "object") {
    return { message: `Request failed: ${status}` };
  }
  const record = data as Record<string, unknown>;
  const msg = typeof record.message === "string" ? record.message.trim() : "";
  const err = typeof record.error === "string" ? record.error.trim() : "";
  const detail = typeof record.detail === "string" ? record.detail.trim() : "";
  const message = msg !== "" ? msg : err !== "" ? err : `Request failed: ${status}`;
  return { message, detail: detail !== "" ? detail : undefined };
}

async function authHeader(): Promise<Record<string, string>> {
  if (!supabase) {
    return {};
  }
  const { data } = await supabase.auth.getSession();
  const token = data.session?.access_token;
  return token ? { Authorization: `Bearer ${token}` } : {};
}

export async function apiFetch<T>(
  path: string,
  options: {
    method?: string;
    body?: unknown;
    query?: Record<string, string | number | boolean | undefined>;
  } = {},
): Promise<T> {
  const headers: Record<string, string> = {
    ...(await authHeader()),
  };
  const body = options.body !== undefined ? JSON.stringify(options.body) : undefined;
  if (body !== undefined) {
    headers["Content-Type"] = "application/json";
  }
  let url = path;
  if (options.query) {
    const search = new URLSearchParams();
    for (const [key, value] of Object.entries(options.query)) {
      if (value === undefined) {
        continue;
      }
      search.append(key, String(value));
    }
    const query = search.toString();
    if (query) {
      url += `?${query}`;
    }
  }

  const response = await fetch(url, {
    method: options.method ?? "GET",
    headers,
    body,
  });

  const text = await response.text();
  let data: unknown = undefined;
  if (text.length > 0) {
    try {
      data = JSON.parse(text) as unknown;
    } catch {
      data = text;
    }
  }

  if (!response.ok) {
    const parsed = parseApiErrorPayload(response.status, data);
    throw new ApiError(response.status, parsed.message, data);
  }

  return data as T;
}
